import React from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getTechniqueName, sortTechniqueCategories } from '@/shared/hooks/useTechniques';

export interface ITrainingTechniquesSectionProps {
  selectedTechniques: string[];
  setSelectedTechniques: (techniques: string[] | ((prev: string[]) => string[])) => void;
  showTechniques: boolean;
  setShowTechniques: (show: boolean) => void;
  openCategories: string[];
  setOpenCategories: (categories: string[] | ((prev: string[]) => string[])) => void;
  techniqueCategories: any;
  techniquesLoading: boolean;
}

export const TrainingTechniquesSection: React.FC<ITrainingTechniquesSectionProps> = ({
  selectedTechniques,
  setSelectedTechniques,
  showTechniques,
  setShowTechniques,
  openCategories,
  setOpenCategories,
  techniqueCategories,
  techniquesLoading,
}) => {
  const { t, currentLanguage } = useI18n();

  const handleTechniqueSelect = (technique: string): void => {
    setSelectedTechniques(prev => {
      if (prev.includes(technique)) {
        return prev.filter(t => t !== technique);
      } else {
        return [...prev, technique];
      }
    });
  };

  const toggleCategory = (categoryName: string): void => {
    setOpenCategories(prev => {
      if (prev.includes(categoryName)) {
        return prev.filter(cat => cat !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
  };

  const isCategoryOpen = (categoryName: string): boolean => {
    return openCategories.includes(categoryName);
  };

  const handleShowTechniques = (): void => {
    setShowTechniques(!showTechniques);
    // Scroll to bottom after a short delay to allow content to render
    setTimeout(() => {
      const modalContent = document.querySelector('[data-radix-scroll-area-viewport]');
      if (modalContent) {
        modalContent.scrollTo({
          top: modalContent.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  return (
    <div className="rounded-lg !mt-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <label className="text-sm font-medium text-black">
            {t('calendar.link_techniques')}
          </label>
        </div>
        <button
          type="button"
          onClick={handleShowTechniques}
          className="text-xs text-sossiety-red hover:text-sossiety-red-hover font-medium underline"
        >
          {showTechniques ? t('calendar.hide_techniques') : t('calendar.show_techniques')}
        </button>
      </div>
      
      {!showTechniques && selectedTechniques.length === 0 && (
        <div className="text-center mt-3 mb-3">
          <p className="text-xs font-medium text-gray-600">
            {t('calendar.register_techniques_used')}
          </p>
          <p className="text-xs text-gray-600">
            {t('calendar.techniques_filter_description')}
          </p>
        </div>
      )}

      {selectedTechniques.length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-gray-600 mb-2">
            {(() => {
              const plural = selectedTechniques.length !== 1 ? 's' : '';
              const pluralSelected = currentLanguage === 'pt' && selectedTechniques.length !== 1 ? 's' : '';
              return t('calendar.selected_techniques_count', { 
                count: selectedTechniques.length,
                plural: plural,
                plural_selected: pluralSelected
              }).replace(/\{plural\}/g, plural).replace(/\{plural_selected\}/g, pluralSelected);
            })()}
          </div>
          <div className="flex flex-wrap gap-1">
            {selectedTechniques.map((techId) => {
              // Find the technique name by unique ID (category_index format)
              let technique = null;
              const lastUnderscoreIndex = techId.lastIndexOf('_');
              const categoryName = techId.substring(0, lastUnderscoreIndex);
              const indexStr = techId.substring(lastUnderscoreIndex + 1);
              const index = parseInt(indexStr) - 1;
              
              const category = (techniqueCategories as any[])?.find(cat => cat.name === categoryName);
              if (category && category.techniques && category.techniques[index]) {
                technique = category.techniques[index];
              }
              
              // Use the same translation function as the collapsible buttons
              const techniqueName = technique ? getTechniqueName(technique, currentLanguage) : techId;
              
              return (
                <span
                  key={techId}
                  className="px-2 py-1 bg-sossiety-red text-white rounded-full text-xs font-medium"
                >
                  {techniqueName}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {showTechniques && (
        <div className="space-y-3 mt-4">
          {techniquesLoading ? (
            <div className="text-center py-4 text-gray-500">Loading techniques...</div>
          ) : !techniqueCategories || (techniqueCategories as any[]).length === 0 ? (
            <div className="text-center py-4 text-gray-500">No techniques available</div>
          ) : (
            sortTechniqueCategories(techniqueCategories as any[])?.map((category) => (
            <Collapsible 
              key={category.name} 
              open={isCategoryOpen(category.name)} 
              onOpenChange={() => toggleCategory(category.name)}
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-left text-black bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                <span>{t(`calendar.technique_category_names.${category.name}`) || category.name}</span>
                {isCategoryOpen(category.name) ? (
                  <ChevronUp className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-500" />
                )}
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <div className="flex flex-wrap gap-1">
                  {category.techniques.map((technique: any, index: number) => {
                    // Create unique technique ID by combining category and index
                    const techniqueId = `${category.name}_${index + 1}`;
                    const isSelected = selectedTechniques.includes(techniqueId);
                    return (
                      <button
                        key={technique.id}
                        type="button"
                        onClick={() => handleTechniqueSelect(techniqueId)}
                        className={`px-2 py-1 text-xs font-medium rounded border transition-colors duration-200 ${
                          isSelected
                            ? 'bg-sossiety-red text-white border-sossiety-red'
                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                      >
                        {getTechniqueName(technique, currentLanguage)}
                      </button>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
            ))
          )}
        </div>
      )}
    </div>
  );
};

